import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_LABELS = {
  "paid-call": "Paid Strategy Call",
  audit: "Nightclub Audit",
  "pre-launch": "Pre-Launch Consulting",
  "quick-chat": "Free 3-minute Quick Chat",
  general: "General Enquiry",
};

export default function ContactForm({ initialService = "general", onSubmitted }) {
  const [form, setForm] = useState({
    name: "",
    venue: "",
    city: "",
    email: "",
    phone: "",
    challenge: "",
  });
  const [service, setService] = useState(initialService);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (initialService && !done) setService(initialService);
  }, [initialService, done]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.challenge) {
      toast.error("Please fill in name, email, phone and your challenge.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        ...form,
        service_interest: service,
      });
      setDone(true);
      toast.success("Received — I'll be in touch personally.");
      onSubmitted?.(res.data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        data-testid="contact-success"
        className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-b from-[#141005] to-[#0a0a0a] p-10 md:p-14 text-center"
      >
        <div className="mx-auto w-14 h-14 rounded-full border border-[#D4AF37]/50 flex items-center justify-center mb-6">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12l5 5L20 7"
              stroke="#D4AF37"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="font-display text-white text-3xl md:text-5xl leading-tight">
          Message received.
        </h3>
        <p className="mt-4 text-white/70 max-w-md mx-auto">
          Your note is now in my inbox. I read every inbound personally and
          reply within 24 hours (usually much faster).
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="rounded-2xl border border-white/8 bg-white/[0.02] backdrop-blur-xl p-5 sm:p-6 md:p-10 lg:p-14"
    >
      {/* Service pill selector */}
      <div className="-mx-1 mb-8 md:mb-10 flex gap-2 overflow-x-auto pb-2 flex-nowrap md:flex-wrap md:overflow-visible md:pb-0 md:mx-0 scrollbar-hide">
        {Object.entries(SERVICE_LABELS).map(([k, label]) => (
          <button
            type="button"
            key={k}
            onClick={() => setService(k)}
            data-testid={`service-select-${k}`}
            className={`shrink-0 text-[12px] tracking-wide px-3.5 py-2 rounded-full border transition-colors ${
              service === k
                ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:gap-x-8 gap-y-5 md:gap-y-6">
        <Field
          label="Your name"
          placeholder="Jane Doe"
          value={form.name}
          onChange={set("name")}
          testId="input-name"
          required
        />
        <Field
          label="Venue"
          placeholder="Name of your venue"
          value={form.venue}
          onChange={set("venue")}
          testId="input-venue"
        />
        <Field
          label="City"
          placeholder="Mumbai / Delhi / Bengaluru…"
          value={form.city}
          onChange={set("city")}
          testId="input-city"
        />
        <Field
          label="Email"
          type="email"
          placeholder="you@venue.com"
          value={form.email}
          onChange={set("email")}
          testId="input-email"
          required
        />
        <Field
          label="Phone / WhatsApp"
          placeholder="+91 …"
          value={form.phone}
          onChange={set("phone")}
          testId="input-phone"
          required
        />
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.28em] text-white/45">
            The business challenge
          </label>
          <textarea
            value={form.challenge}
            onChange={set("challenge")}
            data-testid="input-challenge"
            rows={4}
            required
            placeholder="What are you trying to fix or grow?"
            className="line-input mt-1 resize-none"
          />
        </div>
      </div>

      <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 sm:gap-6">
        <p className="text-white/45 text-xs md:text-[13px] max-w-sm leading-relaxed">
          By submitting, you agree we may contact you about your venue. We
          don't share your data.
        </p>
        <button
          type="submit"
          disabled={loading}
          data-testid="submit-lead"
          className="group inline-flex items-center justify-center sm:justify-start gap-3 rounded-full bg-[#D4AF37] text-black px-6 sm:px-7 py-3.5 sm:py-4 text-sm font-medium hover:bg-[#f3d568] transition-colors disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
        >
          {loading ? "Sending…" : "Send Message"}
          <span className="w-6 h-6 rounded-full bg-black/10 flex items-center justify-center group-hover:translate-x-1 transition-transform">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12h14M13 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </span>
        </button>
      </div>
    </form>
  );
}

function Field({ label, testId, type = "text", ...rest }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.28em] text-white/45">
        {label}
      </label>
      <input
        {...rest}
        type={type}
        data-testid={testId}
        className="line-input mt-1"
      />
    </div>
  );
}
